import { Button } from "@msuite/ui/button";
import { TextInput } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { WorkspaceSchema } from "@msuite/utils/schemas";
import { useFormContext, useWatch } from "react-hook-form";

export function WorkspaceFormLegalInformation() {
	/** Context */
	const methods = useFormContext<WorkspaceSchema>();
	const legal = useWatch({ control: methods.control, name: "legal" });

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const legalBlocks = legal?.blocks ?? [];

	/** Functions */
	function handleOnAddBlock() {
		const currentBlocks = methods.getValues("legal.blocks");
		if (!currentBlocks) {
			methods.setValue("legal.blocks", [[""]]);
			return;
		}
		methods.setValue("legal.blocks", [...legalBlocks, [""]]);
	}

	function handleOnAddLine(blockIndex: number) {
		const newBlocks = [...legalBlocks];
		const block = newBlocks[blockIndex];
		if (!block) return;
		newBlocks[blockIndex] = [...block, ""];
		methods.setValue("legal.blocks", newBlocks);
	}

	function handleOnRemoveLine(blockIndex: number, blockLineIndex: number) {
		const newBlocks = [...legalBlocks];
		const block = newBlocks[blockIndex];
		if (!block) return;
		newBlocks[blockIndex] = block.filter((_, index) => index !== blockLineIndex);
		if (newBlocks[blockIndex].length === 0) {
			newBlocks.splice(blockIndex, 1);
		}
		methods.setValue("legal.blocks", newBlocks);
	}

	function handleOnRemoveBlock(blockIndex: number) {
		const newBlocks = [...legalBlocks];
		newBlocks.splice(blockIndex, 1);
		methods.setValue("legal.blocks", newBlocks);
	}

	return (
		<div className="v-stack gap-4">
			{legalBlocks.map((block, blockIndex) => (
				<div
					key={`legal-block-${blockIndex}`}
					className="v-stack"
				>
					<div className="h-stack justify-between">
						<p className="label">Block {blockIndex + 1}</p>
						<Button
							variant="plain"
							className="mt-2"
							onClick={() => {
								handleOnRemoveBlock(blockIndex);
							}}
							icon={
								<Icon
									name="Trash2"
									className="stroke-red-500"
								/>
							}
						/>
					</div>
					<div className="v-stack card p-0 overflow-hidden divide-y divide-primary-500/10 gap-0">
						{block.map((_, blockLineIndex) => (
							<TextInput
								className="border-none rounded-none shadow-none !ring-0"
								key={`block-${blockIndex}-line-${blockLineIndex}`}
								control={methods.control}
								name={`legal.blocks.${blockIndex}.${blockLineIndex}`}
								isRequired={false}
								placeholder="Enter legal text"
								label={undefined}
								allowOnIconClick
								leftIcon={() => (
									<div
										onClick={() => {
											handleOnRemoveLine(blockIndex, blockLineIndex);
										}}
									>
										<Icon
											name="Trash2"
											className="stroke-red-500 cursor-pointer"
										/>
									</div>
								)}
							/>
						))}
						<div className="px-4 py-2">
							<Button
								size="xs"
								onClick={() => {
									handleOnAddLine(blockIndex);
								}}
								variant="plain"
							>
								{t("misc.add_line")}
							</Button>
						</div>
					</div>
				</div>
			))}
			<Button
				size="xs"
				onClick={handleOnAddBlock}
				className="w-max"
				data-cy="legal-information-add-block-button"
			>
				{t("misc.add_block")}
			</Button>
		</div>
	);
}
